/**
 * <s-reveal-more> — shows a masonry wall in batches with stable placement.
 *
 * Children of [data-reveal-list] are dealt into fixed column stacks in source
 * order (tile 1 → column 1, tile 2 → column 2, …), so a tile never jumps to
 * another column when media loads or more tiles are revealed. Columns: 2 on
 * phones, 3 from 768px, 4 from 1024px; they are rebuilt only when that count
 * changes.
 *
 * Tiles beyond data-initial (default 12) start hidden; a commandfor button
 * with command="--more" reveals the next data-step (default 6). While more
 * remain, the wall is trimmed to its shortest column so every column runs
 * into the fade with no empty spots. The control hides once everything is
 * visible. Without JS the CSS column fallback on the list shows every tile.
 */
const COLUMN_QUERIES = [
  { query: '(min-width: 1024px)', count: 4 },
  { query: '(min-width: 768px)', count: 3 },
];
const LIST_CLASS = 'flex items-start gap-2 md:gap-3 lg:gap-5';
const COLUMN_CLASS = 'flex min-w-0 flex-1 basis-0 flex-col gap-2 md:gap-3 lg:gap-5';
// How far above the shortest column's end the wall is cut, so its last tile still peeks into the fade.
const TRIM_OFFSET = 48;

class SRevealMore extends HTMLElement {
  #ac;
  #shown = 0;
  #columnCount = 0;
  /** @type {HTMLElement[]} */
  #tiles = [];
  /** @type {HTMLElement[]} */
  #columns = [];
  /** @type {ResizeObserver | undefined} */
  #observer;
  #frame = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const signal = this.#ac.signal;
    const list = this.#list();
    if (!list) return;

    this.#tiles = /** @type {HTMLElement[]} */ ([...list.children]);
    this.#shown = Number(this.dataset.initial) || 12;

    this.addEventListener('command', (event) => {
      if (/** @type {Event & { command?: string }} */ (event).command === '--more') this.#reveal(this.#step());
    }, { signal });

    COLUMN_QUERIES.forEach(({ query }) => {
      window.matchMedia(query).addEventListener('change', () => this.#layout(), { signal });
    });

    this.#observer = new ResizeObserver(() => this.#scheduleTrim());
    this.#layout();
    this.#apply(false);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#frame);
  }

  #list() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-reveal-list]'));
  }

  #step() {
    return Number(this.dataset.step) || 6;
  }

  #countForViewport() {
    const match = COLUMN_QUERIES.find(({ query }) => window.matchMedia(query).matches);
    return match ? match.count : 2;
  }

  /** Deals tiles into column stacks in source order; only rebuilds when the column count changes. */
  #layout() {
    const list = this.#list();
    if (!list) return;
    const count = this.#countForViewport();
    if (count === this.#columnCount) return;
    this.#columnCount = count;

    this.#observer?.disconnect();
    this.#columns = Array.from({ length: count }, () => {
      const column = document.createElement('div');
      column.className = COLUMN_CLASS;
      return column;
    });
    this.#tiles.forEach((tile, i) => this.#columns[i % count].append(tile));
    list.className = LIST_CLASS;
    list.replaceChildren(...this.#columns);
    this.#columns.forEach((column) => this.#observer?.observe(column));
    this.#scheduleTrim();
  }

  /** @param {number} count */
  #reveal(count) {
    const previous = this.#shown;
    this.#shown += count;
    this.#apply(true, previous);
  }

  /**
   * @param {boolean} focusFirstNew
   * @param {number} [firstNew]
   */
  #apply(focusFirstNew, firstNew = 0) {
    this.#tiles.forEach((tile, i) => tile.toggleAttribute('hidden', i >= this.#shown));
    const done = this.#shown >= this.#tiles.length;
    this.querySelectorAll('[data-reveal-control]').forEach((control) => control.toggleAttribute('hidden', done));
    this.#scheduleTrim();
    if (focusFirstNew) {
      const target = this.#tiles[firstNew]?.querySelector('a, button, select, video');
      if (target instanceof HTMLElement && target.tabIndex >= 0) target.focus({ preventScroll: true });
    }
  }

  #scheduleTrim() {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#trim());
  }

  /** While tiles remain hidden, cut the wall at the shortest column so no column ends early. */
  #trim() {
    const list = this.#list();
    if (!list) return;
    const done = this.#shown >= this.#tiles.length;
    if (done || this.#columns.length === 0) {
      list.style.maxHeight = '';
      list.style.overflow = '';
      return;
    }
    const heights = this.#columns.map((column) => column.scrollHeight).filter((height) => height > 0);
    if (heights.length === 0) return;
    const shortest = Math.min(...heights);
    list.style.maxHeight = `${Math.max(shortest - TRIM_OFFSET, 0)}px`;
    list.style.overflow = 'hidden';
  }
}

if (!customElements.get('s-reveal-more')) customElements.define('s-reveal-more', SRevealMore);
